// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * Rewrite Optix All Dialog for I-9 documents
 *
 * Re-pushes the promoted run's metadata to Optix for every document whose last
 * write-back failed — no re-OCR, no re-match. The failed set spans every tab (a write
 * can fail on any matched/partial/no-match document), so unlike Rematch all this is not
 * tab-scoped. Each document is rewritten in its own processor run; documents already in
 * flight (or shredded/purged) are skipped by the server. Takes no options — a plain
 * confirmation.
 */
import type { FC } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Button,
} from '@/components/ui'
import { Upload } from 'lucide-react'

interface RewriteOptixAllDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  isRewriting: boolean
  onConfirm: () => void
}

export const RewriteOptixAllDialog: FC<RewriteOptixAllDialogProps> = ({
  open,
  onOpenChange,
  isRewriting,
  onConfirm,
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="sm:max-w-md">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <Upload className="h-5 w-5" />
          Re-run all Optix failures
        </DialogTitle>
        <DialogDescription>
          Re-push the stored result to Optix for every document whose last metadata
          write-back failed, across all tabs — no re-OCR or re-match. Each document is
          rewritten in its own run; documents already being processed are skipped
          automatically.
        </DialogDescription>
      </DialogHeader>

      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)}>
          Cancel
        </Button>
        <Button onClick={onConfirm} disabled={isRewriting}>
          <Upload className="h-4 w-4 mr-1" />
          Re-run all Optix failures
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
)
